import React, { useEffect, useMemo, useState } from "react";
import {
    Sun,
    Cloud,
    CloudSun,
    CloudFog,
    CloudDrizzle,
    CloudRain,
    CloudHail,
    CloudSnow,
    CloudLightning,
    Zap,
    Loader2,
    Droplets,
    Wind,
    Sunset,
    Sunrise as SunriseIcon,
    Umbrella,
} from "lucide-react";
import { api } from "@/api";

const iconMap = {
    "sun": Sun,
    "cloud": Cloud,
    "cloud-sun": CloudSun,
    "cloud-fog": CloudFog,
    "cloud-drizzle": CloudDrizzle,
    "cloud-rain": CloudRain,
    "cloud-hail": CloudHail,
    "cloud-snow": CloudSnow,
    "cloud-lightning": CloudLightning,
    "zap": Zap,
};

/** Rich, colourful icon for a WMO weather code (styled like Carrot Weather). */
const WeatherIcon = ({ code, isDay = true, size = 72 }) => {
    // Palette per condition family
    let primary = "#38bdf8"; // sky-400
    let accent = "#fde047";  // yellow-300 (sun)
    let Comp = iconMap["cloud-sun"];
    if (code === 0) {
        Comp = Sun;
        primary = isDay ? "#fbbf24" : "#c4b5fd";
    } else if (code === 1 || code === 2) {
        Comp = CloudSun;
        primary = "#fbbf24";
        accent = "#38bdf8";
    } else if (code === 3) {
        Comp = Cloud;
        primary = "#94a3b8";
    } else if (code === 45 || code === 48) {
        Comp = CloudFog;
        primary = "#cbd5e1";
    } else if ([51, 53, 55, 56, 57].includes(code)) {
        Comp = CloudDrizzle;
        primary = "#38bdf8";
    } else if ([61, 63, 65, 80, 81, 82].includes(code)) {
        Comp = CloudRain;
        primary = "#0ea5e9";
    } else if ([66, 67].includes(code)) {
        Comp = CloudHail;
        primary = "#818cf8";
    } else if ([71, 73, 75, 77, 85, 86].includes(code)) {
        Comp = CloudSnow;
        primary = "#a5f3fc";
    } else if (code === 95) {
        Comp = CloudLightning;
        primary = "#7c3aed";
        accent = "#fde047";
    } else if ([96, 99].includes(code)) {
        Comp = Zap;
        primary = "#eab308";
    }
    return (
        <div className="relative" style={{ width: size, height: size }} aria-hidden="true">
            <Comp size={size} color={primary} strokeWidth={1.6} className="drop-shadow-[0_0_12px_currentColor]" />
        </div>
    );
};

const toneBg = {
    fuchsia: "from-fuchsia-500/25 via-fuchsia-500/5",
    violet: "from-violet-500/25 via-violet-500/5",
    cyan: "from-cyan-500/25 via-cyan-500/5",
    sky: "from-sky-500/30 via-sky-500/5",
    red: "from-red-500/25 via-red-500/5",
    emerald: "from-emerald-500/20 via-emerald-500/5",
    rose: "from-rose-500/25 via-rose-500/5",
    indigo: "from-indigo-500/25 via-indigo-500/5",
};

/** Rich weather card that replaces the "clear skies" empty state on the screensaver.
 *  Focuses on the day matching `targetDate` (ISO YYYY-MM-DD) if provided,
 *  otherwise shows current + 5-day outlook.
 */
export const WeatherCard = ({ targetDate, tone = "cyan", label = "" }) => {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                // First: use cached lat/lon if we have it
                let lat = null, lon = null;
                try {
                    const cached = localStorage.getItem("meye_geo");
                    if (cached) {
                        const g = JSON.parse(cached);
                        // Cache 24 hours
                        if (g?.lat && g?.lon && Date.now() - (g.ts || 0) < 86400000) {
                            lat = g.lat; lon = g.lon;
                        }
                    }
                } catch { /* ignore */ }

                // Second: ask the browser directly (uses client IP which actually geolocates)
                if (lat == null || lon == null) {
                    try {
                        const r = await fetch("https://ipapi.co/json/", { cache: "no-store" });
                        if (r.ok) {
                            const g = await r.json();
                            if (g.latitude && g.longitude) {
                                lat = g.latitude;
                                lon = g.longitude;
                                try {
                                    localStorage.setItem("meye_geo", JSON.stringify({
                                        lat, lon, city: g.city, ts: Date.now(),
                                    }));
                                } catch { /* ignore */ }
                            }
                        }
                    } catch { /* ipapi may be blocked — fall through */ }
                }

                // Last resort: ask the browser for real GPS if user grants it (not intrusive — silent fail)
                if ((lat == null || lon == null) && navigator.geolocation) {
                    lat = lon = await new Promise((resolve) => {
                        navigator.geolocation.getCurrentPosition(
                            (pos) => resolve(pos.coords),
                            () => resolve(null),
                            { timeout: 5000, maximumAge: 3600000 },
                        );
                    }).then((c) => (c ? { latitude: c.latitude, longitude: c.longitude } : null));
                    if (lat && typeof lat === "object") {
                        const c = lat;
                        lat = c.latitude;
                        lon = c.longitude;
                    } else {
                        lat = lon = null;
                    }
                }

                const w = await api.getWeather({ lat, lon, days: 8 });
                if (!cancelled) setData(w);
            } catch (e) {
                if (!cancelled) setErr(e?.response?.data?.detail || "weather unavailable");
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const focus = useMemo(() => {
        if (!data) return null;
        if (targetDate) {
            const d = data.days?.find((x) => x.date === targetDate);
            if (d) return d;
        }
        return data.days?.[0] || null;
    }, [data, targetDate]);

    const gradient = toneBg[tone] || toneBg.cyan;

    if (err) {
        return (
            <div className="text-center fade-up" data-testid="weather-card-error">
                <p className="font-brand text-2xl text-zinc-400">{label || "clear skies ahead."}</p>
                <p className="text-[10px] uppercase tracking-widest text-zinc-600 font-mono mt-2">{err}</p>
            </div>
        );
    }
    if (!data || !focus) {
        return (
            <div className="text-center fade-up" data-testid="weather-card-loading">
                <Loader2 className="mx-auto animate-spin text-meye-cyan" size={22} />
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mt-2">reading the sky…</p>
            </div>
        );
    }

    const now = data.current || {};
    const isToday = focus.date === new Date().toISOString().slice(0, 10);
    const dateLabel = (() => {
        try {
            const d = new Date(`${focus.date}T12:00:00`);
            return d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
        } catch {
            return focus.date;
        }
    })();

    // Sunrise / sunset display
    const fmtTime = (iso) => {
        if (!iso) return "";
        try {
            return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
        } catch { return ""; }
    };

    return (
        <div
            className={`w-full max-w-md mx-auto rounded-2xl border border-white/10 bg-gradient-to-b ${gradient} to-black/60 backdrop-blur-md p-5 fade-up`}
            data-testid="weather-card"
        >
            {/* Header: city + date */}
            <div className="flex items-center justify-between mb-3">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] font-mono text-zinc-400">
                        {data.city || "your location"}
                    </p>
                    <p className="font-brand text-lg font-bold text-zinc-100 tracking-tight">
                        {dateLabel}
                    </p>
                </div>
                <div className="text-right">
                    <p className="font-brand text-[10px] uppercase tracking-widest text-zinc-500">
                        {isToday ? "now" : ""}
                    </p>
                </div>
            </div>

            {/* Big current temperature + icon (for today) or forecast (other days) */}
            <div className="flex items-center gap-4">
                <WeatherIcon
                    code={isToday ? now.code : focus.code}
                    isDay={isToday ? now.is_day : true}
                    size={84}
                />
                <div className="flex-1">
                    {isToday && now.temp != null ? (
                        <>
                            <p className="font-brand text-6xl font-bold text-white leading-none tracking-tight">
                                {Math.round(now.temp)}°
                            </p>
                            <p className="text-xs text-zinc-400 mt-1 font-mono">
                                Feels {Math.round(now.feels_like ?? now.temp)}°
                            </p>
                            <p className="text-sm text-zinc-200 mt-1">{now.summary}</p>
                        </>
                    ) : (
                        <>
                            <p className="font-brand text-3xl font-bold text-white leading-none tracking-tight">
                                {Math.round(focus.temp_max)}° <span className="text-zinc-500 text-2xl">/ {Math.round(focus.temp_min)}°</span>
                            </p>
                            <p className="text-sm text-zinc-200 mt-1">{focus.summary}</p>
                            {focus.precip_prob > 0 && (
                                <p className="text-[10px] text-sky-300 uppercase tracking-widest font-mono mt-1">
                                    {focus.precip_prob}% chance of rain
                                </p>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-4 gap-2 mt-4">
                <Stat icon={<Umbrella size={12} />} label="rain" value={`${Math.round(focus.precip_mm ?? 0)}mm`} tone="text-sky-300" />
                <Stat icon={<Droplets size={12} />} label="prob" value={`${focus.precip_prob ?? 0}%`} tone="text-cyan-300" />
                <Stat icon={<Wind size={12} />} label="wind" value={`${Math.round(focus.wind_max ?? 0)} km/h`} tone="text-zinc-300" />
                <Stat icon={<Sun size={12} />} label="uv" value={`${Math.round(focus.uv_max ?? 0)}`} tone={
                    (focus.uv_max ?? 0) >= 6 ? "text-amber-300" : "text-zinc-300"
                } />
            </div>

            {/* Sunrise / sunset when we have data */}
            {focus.sunrise && (
                <div className="grid grid-cols-2 gap-2 mt-2 text-[10px] uppercase tracking-widest font-mono">
                    <div className="flex items-center gap-1.5 text-amber-300">
                        <SunriseIcon size={11} /> {fmtTime(focus.sunrise)}
                    </div>
                    <div className="flex items-center gap-1.5 text-violet-300 justify-end">
                        <Sunset size={11} /> {fmtTime(focus.sunset)}
                    </div>
                </div>
            )}

            {/* Multi-day preview strip */}
            {data.days?.length > 1 && (
                <div className="mt-4 pt-4 border-t border-white/10 flex gap-2 overflow-x-auto">
                    {data.days.slice(0, 7).map((d) => {
                        const dayLabel = (() => {
                            try {
                                return new Date(`${d.date}T12:00:00`).toLocaleDateString(undefined, { weekday: "short" });
                            } catch { return d.date; }
                        })();
                        const active = d.date === focus.date;
                        return (
                            <div
                                key={d.date}
                                className={`flex-shrink-0 flex flex-col items-center justify-center min-w-[54px] rounded-lg px-2 py-2 ${
                                    active ? "bg-white/10" : ""
                                }`}
                                data-testid={`weather-day-${d.date}`}
                            >
                                <span className="text-[10px] font-brand font-bold text-zinc-400 uppercase tracking-wider">{dayLabel}</span>
                                <div className="my-1">
                                    <WeatherIcon code={d.code} size={26} />
                                </div>
                                <span className="text-[10px] text-white font-mono">
                                    {Math.round(d.temp_max)}°
                                </span>
                                <span className="text-[9px] text-zinc-500 font-mono">
                                    {Math.round(d.temp_min)}°
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const Stat = ({ icon, label, value, tone = "text-zinc-300" }) => (
    <div className="rounded-lg bg-white/5 border border-white/10 p-2 flex flex-col items-center text-center">
        <div className={`flex items-center gap-1 ${tone} text-[10px] font-mono uppercase tracking-widest`}>
            {icon}
            {label}
        </div>
        <div className="text-xs font-brand font-bold text-white mt-0.5">{value}</div>
    </div>
);

export default WeatherCard;
